import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search,
    User,
    Ticket,
    History,
    Shield,
    Mail,
    Phone,
    Calendar,
    MapPin,
    Award,
    Clock,
    Edit,
    Save,
    X,
    FileText,
    AlertTriangle,
    CheckCircle,
    Users,
    Crown,
    ArrowLeft,
    Gamepad2,
    Eye,
    EyeOff,
    Plus,
    Trash2,
    Gavel,
    AlertCircle,
    ArrowRight,
    Send,
    CheckSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { safeParseObject } from '../../utils/storageUtils.js';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const StaffPlayerLookup = () => {
    const { tickets, createTicket } = useTickets();
    const { user: currentStaff } = useAuth();
    const staffName = getStaffDisplayName(currentStaff);
    const navigate = useNavigate();

    // States for Search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState('');

    // States for Detailed View
    const [selectedUser, setSelectedUser] = useState(null);
    const [userTickets, setUserTickets] = useState([]);
    const [userHistory, setUserHistory] = useState([]);
    const [staffNotes, setStaffNotes] = useState('');
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [tempNotes, setTempNotes] = useState('');

    // Privacy State
    const [showPrivateInfo, setShowPrivateInfo] = useState(false);

    // Server Management State
    const [isEditingServers, setIsEditingServers] = useState(false);
    const [editedCities, setEditedCities] = useState([]);
    const [isAddingCity, setIsAddingCity] = useState(false);
    const [newCity, setNewCity] = useState({ city: '', characterName: '', playerId: '' });

    // Punishment System State
    const [isPunishmentModalOpen, setIsPunishmentModalOpen] = useState(false);
    const [punishmentStep, setPunishmentStep] = useState(1);
    const [punishmentData, setPunishmentData] = useState({
        serverProfileId: '', // Format: "City|PlayerID|CharName"
        type: '',
        wl: '',
        reason: '',
        attachments: '',
        priority: 'normal',
        deadlineType: 'indefinite', // 'date' or 'indefinite'
        deadlineDate: '',
        targetDepartment: ''
    });

    const punishmentTypes = [
        { value: 'ban_indeterminado', label: 'Banimento Indeterminado', color: 'red' },
        { value: 'suspensao_temporaria', label: 'Suspensão Temporária', color: 'orange' },
        { value: 'amarracao', label: 'Amarração', color: 'yellow' },
        { value: 'intimacao', label: 'Intimação', color: 'blue' },
        { value: 'alinhamento', label: 'Alinhamento', color: 'green' },
        { value: 'solicitar_contato', label: 'Solicitar Contato', color: 'cyan' }
    ];

    const departments = [
        { value: 'head_moderacao', label: 'Head de Moderação' },
        { value: 'head_suporte', label: 'Head de Suporte' },
        { value: 'head_staff', label: 'Head Staff' },
        { value: 'direcao', label: 'Direção' },
        { value: 'admin', label: 'Administrador' }
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchError('');

        if (!searchTerm.trim()) return;

        // 1. Validate Numeric Input (Strict)
        const isNumeric = /^\d+$/.test(searchTerm);
        if (!isNumeric) {
            setSearchError('A busca permite APENAS números (ID do Discord ou ID do Jogador).');
            return;
        }

        setHasSearched(true);
        setSelectedUser(null);
        setShowPrivateInfo(false);

        const savedUsers = safeParseObject('registeredUsers');

        const results = Object.values(savedUsers).filter(u => {
            const discordIdMatch = u.discordId?.includes(searchTerm);
            const playerIdMatch = u.cities?.some(c => c.playerId?.toString() === searchTerm);
            return discordIdMatch || playerIdMatch;
        });

        setSearchResults(results);
    };

    const handleSelectUser = (user) => {
        const foundTickets = tickets.filter(t =>
            t.userId === user.discordId ||
            t.authorName?.toLowerCase() === user.name?.toLowerCase()
        );

        setUserTickets(foundTickets);

        const history = safeParseObject('user_history');
        const userHistoryEntries = history[user.discordId] || [];
        setUserHistory(userHistoryEntries);

        const notes = localStorage.getItem(`staff_notes_${user.discordId}`) || '';
        setStaffNotes(notes);
        setTempNotes(notes);

        setEditedCities(user.cities || []);

        setSelectedUser({
            ...user,
            role: getRoleName(user.accessLevel),
            joinDate: user.joinDate || user.createdAt,
            warnings: user.warnings || 0,
            bans: user.bans || 0
        });
    };

    const handleBackToResults = () => {
        setSelectedUser(null);
    };

    const getRoleName = (accessLevel) => {
        const roleNames = {
            'user': 'Cidadão',
            'suporte': 'Suporte',
            'suporte_compras': 'Suporte - Compras',
            'suporte_descontos': 'Suporte - Descontos',
            'suporte_tecnico': 'Suporte Técnico',
            'moderador': 'Moderador',
            'super_moderador': 'Super Moderador',
            'programador': 'Programador',
            'head_suporte': 'Head de Suporte',
            'head_moderacao': 'Head de Moderação',
            'head_staff': 'Head Staff',
            'direcao': 'Direção',
            'admin': 'Administrador'
        };
        return roleNames[accessLevel] || 'Cidadão';
    };

    const handleSaveNotes = () => {
        if (!selectedUser) return;

        localStorage.setItem(`staff_notes_${selectedUser.discordId}`, tempNotes);
        setStaffNotes(tempNotes);
        setIsEditingNotes(false);

        logHistoryAction('staff_note', 'Anotação adicionada/editada');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getChangeTypeLabel = (type) => {
        const labels = {
            'email': 'Email',
            'avatar': 'Avatar',
            'name': 'Nome',
            'staff_note': 'Anotação Staff',
            'frozen_data_admin': 'Dados Congelados (Admin)',
            'privacy_access': 'Acesso a Dados',
            'server_update': 'Atualização de Servidor',
            'punishment_request': 'Ação Disciplinar (Solicitada)',
            'punishment_applied': 'Ação Disciplinar (Aplicada)'
        };
        return labels[type] || type;
    };

    const handleTogglePrivacy = () => {
        if (!showPrivateInfo) {
            logHistoryAction('privacy_access', `Acessou dados sensíveis (Email/Telefone)`);
            alert(`Atenção: O acesso aos dados sensíveis de ${selectedUser.name} foi registrado no histórico.`);
        }
        setShowPrivateInfo(!showPrivateInfo);
    };

    const handleSaveServers = () => {
        if (!selectedUser) return;

        const updatedUser = { ...selectedUser, cities: editedCities };
        setSelectedUser(updatedUser);

        const savedUsers = safeParseObject('registeredUsers');
        if (savedUsers[selectedUser.discordId]) {
            savedUsers[selectedUser.discordId].cities = editedCities;
            localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));
        }

        logHistoryAction('server_update', 'Atualizou informações de servidores/cidades');

        setIsEditingServers(false);
        alert('Informações de servidores atualizadas com sucesso!');
    };

    const handleAddCity = () => {
        if (!newCity.city || !newCity.characterName || !newCity.playerId) {
            alert('Preencha todos os campos para adicionar uma cidade.');
            return;
        }
        setEditedCities([...editedCities, { ...newCity, joinedAt: new Date().toISOString() }]);
        setNewCity({ city: '', characterName: '', playerId: '' });
        setIsAddingCity(false);
    };

    const handleRemoveCity = (index) => {
        if (window.confirm('Tem certeza que deseja remover este perfil de cidade?')) {
            const newCities = [...editedCities];
            newCities.splice(index, 1);
            setEditedCities(newCities);
        }
    };

    // --- Punishment System Logic ---

    const openPunishmentModal = () => {
        setPunishmentStep(1);
        setPunishmentData({
            serverProfileId: '',
            type: '',
            wl: '',
            reason: '',
            attachments: '',
            priority: 'normal',
            deadlineType: 'indefinite',
            deadlineDate: '',
            targetDepartment: ''
        });
        setIsPunishmentModalOpen(true);
    };

    const handleForwardPunishment = async () => {
        // Validation for Special Types (Intimação / Alinhamento / Solicitar Contato)
        const isNotificationType = ['intimacao', 'alinhamento', 'solicitar_contato'].includes(punishmentData.type);

        if (!punishmentData.targetDepartment && !isNotificationType) {
            alert('Selecione o departamento responsável.');
            return;
        }

        const [city, playerId, charName] = punishmentData.serverProfileId.split('|');
        const typeLabel = punishmentTypes.find(t => t.value === punishmentData.type)?.label;

        // Logic for Automatic Ticket Creation (Notification Types)
        if (isNotificationType) {
            const notificationTicket = {
                id: `NOT-${Date.now()}`,
                type: 'notification', // Special type for user dashboard display
                title: `[${typeLabel.toUpperCase()}] - Ação Necessária`,
                description: `
**Ação Requerida:** ${typeLabel}
**Motivo:** ${punishmentData.reason}
**Mensagem da Staff:** ${punishmentData.wl || 'Entre em contato via ticket.'}
**Anexos:** ${punishmentData.attachments || 'Nenhum'}

Uma ação de **${typeLabel}** foi registrada para o seu perfil.
Por favor, responda a este ticket para regularizar sua situação.
                 `,
                userId: selectedUser.discordId,
                authorName: selectedUser.name,
                createdBy: currentStaff.discordId,
                createdByName: staffName,
                accessibleBy: ['suporte', 'moderacao'], // Broad access for staff handling
                status: 'aguardando_usuario', // User needs to see this
                category: 'ticket_automatico',
                createdAt: new Date().toISOString(),
                responses: [],
                priority: 'high',
                isAutoCreated: true,
                targetProfile: punishmentData.serverProfileId
            };

            await createTicket(notificationTicket);

            // Also log as a "Punishment" but with a special status so it shows on dashboard
            // reusing handleApplyDirectly logic partly or calling a modified version could work, 
            // but let's just add it to history and active punishments as "Pending Contact"

            const activeNotification = {
                type: punishmentData.type,
                typeLabel: typeLabel,
                characterName: charName,
                playerId: playerId,
                city: city,
                until: 'Até Resolução',
                requestedBy: staffName,
                appliedBy: staffName,
                appliedAt: new Date().toISOString(),
                reason: punishmentData.reason,
                ticketId: notificationTicket.id,
                status: 'pending_contact' // Custom status for dashboard logic
            };

            updateUserWithPunishment(activeNotification);
            logHistoryAction('punishment_request', `Gerou ${typeLabel} (Ticket #${notificationTicket.id})`);

            setIsPunishmentModalOpen(false);
            alert(`${typeLabel} enviada com sucesso! Ticket #${notificationTicket.id} criado.`);
            return;
        }

        // Standard Escalation Logic (Existing)
        const escalationTicket = {
            id: `ESC-${Date.now()}`,
            type: 'escalation',
            escalationType: 'punicao',
            title: `[AÇÃO] ${typeLabel} - ${selectedUser.name}`,
            description: `
**Ação Solicitada:** ${typeLabel}
**Usuário:** ${selectedUser.name} (${selectedUser.discordId})
**Perfil Alvo:** ${charName} (ID: ${playerId}) - ${city}
**Motivo:** ${punishmentData.reason}
**WL:** ${punishmentData.wl || 'N/A'}
**Prioridade:** ${punishmentData.priority}
**Prazo:** ${punishmentData.deadlineType === 'indefinite' ? 'Indeterminado' : punishmentData.deadlineDate}
**Anexos:** ${punishmentData.attachments || 'Nenhum'}
            `,
            userId: selectedUser.discordId,
            authorName: selectedUser.name,
            createdBy: currentStaff.discordId,
            createdByName: staffName,
            accessibleBy: [punishmentData.targetDepartment],
            status: 'aberto',
            category: 'acao_disciplinar',
            createdAt: new Date().toISOString(),
            responses: [],
            punishmentType: punishmentData.type,
            targetProfile: punishmentData.serverProfileId,
            isPunishmentRequest: true,
            reason: punishmentData.reason,
            wl: punishmentData.wl,
            priority: punishmentData.priority,
            deadlineType: punishmentData.deadlineType,
            deadlineDate: punishmentData.deadlineDate,
            attachments: punishmentData.attachments
        };

        await createTicket(escalationTicket);

        logHistoryAction('punishment_request', `Solicitou ação: ${typeLabel} para ${punishmentData.targetDepartment}`);

        setIsPunishmentModalOpen(false);
        alert('Ação encaminhada com sucesso! Uma escalação foi criada.');
    };

    // Helper to update user object with punishment
    const updateUserWithPunishment = (punishment) => {
        const updatedUser = { ...selectedUser };
        if (!updatedUser.activePunishments) updatedUser.activePunishments = [];

        // Remove old if exists (legacy check)
        if (updatedUser.activePunishment) {
            updatedUser.activePunishments.push(updatedUser.activePunishment);
            delete updatedUser.activePunishment;
        }

        updatedUser.activePunishments.unshift(punishment);
        setSelectedUser(updatedUser);

        const savedUsers = safeParseObject('registeredUsers');
        if (savedUsers[selectedUser.discordId]) {
            if (!savedUsers[selectedUser.discordId].activePunishments) {
                savedUsers[selectedUser.discordId].activePunishments = [];
            }
            if (savedUsers[selectedUser.discordId].activePunishment) {
                savedUsers[selectedUser.discordId].activePunishments.push(savedUsers[selectedUser.discordId].activePunishment);
                delete savedUsers[selectedUser.discordId].activePunishment;
            }
            savedUsers[selectedUser.discordId].activePunishments.unshift(punishment);
            localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));
        }
    };

    const handleApplyDirectly = () => {
        const [city, playerId, charName] = punishmentData.serverProfileId.split('|');
        const typeLabel = punishmentTypes.find(t => t.value === punishmentData.type)?.label;
        const deadline = punishmentData.deadlineType === 'indefinite' ? 'Indeterminado' : new Date(punishmentData.deadlineDate).toLocaleDateString('pt-BR');

        // Update User Data with Active Punishment
        const activePunishment = {
            type: punishmentData.type,
            typeLabel: typeLabel,
            characterName: charName,
            playerId: playerId,
            city: city,
            until: deadline,
            requestedBy: staffName,
            appliedBy: staffName,
            appliedAt: new Date().toISOString(),
            reason: punishmentData.reason
        };

        updateUserWithPunishment(activePunishment);

        // 2. Log to History
        logHistoryAction('punishment_applied', `Aplicou manualmente: ${typeLabel} em ${charName} (ID: ${playerId})`);

        setIsPunishmentModalOpen(false);
        alert('Punição aplicada e registrada com sucesso!');
    };

    const logHistoryAction = (type, action) => {
        const timestamp = new Date().toISOString();
        const entry = {
            timestamp,
            type,
            staffName: staffName || 'Staff',
            staffId: currentStaff?.discordId || 'unknown',
            action
        };

        const history = safeParseObject('user_history');
        if (!history[selectedUser.discordId]) {
            history[selectedUser.discordId] = [];
        }
        history[selectedUser.discordId].unshift(entry);
        localStorage.setItem('user_history', JSON.stringify(history));
        setUserHistory(history[selectedUser.discordId]);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white">Consulta de Jogador</h1>
                    <p className="text-gray-400 mt-1">Pesquise histórico completo e informações detalhadas de usuários</p>
                </div>
                {selectedUser && (
                    <div className="flex gap-3">
                        <Button
                            onClick={openPunishmentModal}
                            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                        >
                            <Gavel className="h-4 w-4 mr-2" />
                            Aplicar Ações
                        </Button>
                        <Button
                            onClick={handleBackToResults}
                            variant="outline"
                            className="border-gray-600 text-gray-300 hover:bg-gray-800"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Voltar
                        </Button>
                    </div>
                )}
            </div>

            {!selectedUser && (
                <Card className="bg-gray-900/30 border-gray-700">
                    <CardContent className="p-6">
                        <form onSubmit={handleSearch} className="flex gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <Input
                                    placeholder="Digite APENAS números (ID Discord ou ID Jogador)..."
                                    className="pl-10 bg-black/20 border-gray-600 text-white h-12 text-lg"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchError && <p className="text-red-400 text-sm mt-2 ml-1">{searchError}</p>}
                            </div>
                            <Button type="submit" className="h-12 px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                                Pesquisar
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}

            {!selectedUser && hasSearched && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {searchResults.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {searchResults.map((user) => (
                                <Card
                                    key={user.discordId}
                                    className="bg-gray-800/40 border-gray-700 hover:bg-gray-800/80 hover:border-blue-500/50 transition-all cursor-pointer group"
                                    onClick={() => handleSelectUser(user)}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="h-16 w-16 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-gray-600 group-hover:border-blue-500 transition-colors">
                                                {user.avatar ? (
                                                    <img
                                                        src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png?size=128`}
                                                        alt="Avatar"
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <User className="fallback-icon h-8 w-8 text-gray-400" style={{ display: user.avatar ? 'none' : 'flex' }} />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">{user.name}</h3>
                                                <p className="text-sm text-gray-400">{getRoleName(user.accessLevel)}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-2 text-sm text-gray-400">
                                            <div className="flex items-center">
                                                <Shield className="h-4 w-4 mr-2 text-blue-500/70" />
                                                <span className="truncate">ID: {user.discordId}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Gamepad2 className="h-4 w-4 mr-2 text-purple-500/70" />
                                                <span>{user.cities?.length || 0} Personagens</span>
                                            </div>
                                            {user.cities?.length > 0 && (
                                                <div className="pl-6 text-xs text-gray-500">
                                                    ID Principal: {user.cities[0].playerId}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-900/20 rounded-2xl border border-gray-800">
                            <User className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                            <h3 className="text-xl font-medium text-white">Nenhum usuário encontrado</h3>
                            <p className="text-gray-400">Não encontramos registros com os termos pesquisados.</p>
                        </div>
                    )}
                </motion.div>
            )}

            {selectedUser && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                >
                    {/* Active Punishment Banner */}
                    {/* Active Punishments Banner */}
                    {(selectedUser.activePunishments?.length > 0 || selectedUser.activePunishment) && (
                        <div className="space-y-3">
                            {/* Handle legacy single punishment or new array */}
                            {(selectedUser.activePunishments || [selectedUser.activePunishment]).map((punishment, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 flex items-start gap-4"
                                >
                                    <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <h3 className="text-lg font-bold text-red-400 mb-1">
                                                Punição Ativa: {punishment.typeLabel}
                                            </h3>
                                            {punishment.ticketId && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-7 text-xs"
                                                    onClick={() => navigate(`/staff/tickets/${punishment.ticketId}`)}
                                                >
                                                    <ArrowRight className="h-3 w-3 mr-1" />
                                                    Ver Escalação
                                                </Button>
                                            )}
                                        </div>
                                        <p className="text-gray-300 text-sm">
                                            Este jogador está com o personagem <span className="font-bold text-white">{punishment.characterName}</span> no ID <span className="font-bold text-white">{punishment.playerId}</span> suspenso/punido até <span className="font-bold text-white">{punishment.until}</span>.
                                        </p>
                                        <p className="text-gray-400 text-xs mt-2">
                                            Solicitada por: {punishment.requestedBy} • Aplicada por: {punishment.appliedBy} • Em: {formatDate(punishment.appliedAt)}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    <Card className="bg-gradient-to-br from-gray-900 to-blue-900/20 border-gray-700">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center space-x-6">
                                    <div className="h-32 w-32 rounded-2xl bg-gray-800 flex items-center justify-center border-2 border-blue-500/30 overflow-hidden">
                                        {selectedUser.avatar ? (
                                            <img
                                                src={`https://cdn.discordapp.com/avatars/${selectedUser.discordId}/${selectedUser.avatar}.png?size=256`}
                                                alt="Avatar"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <User className="fallback-icon h-16 w-16 text-blue-400" style={{ display: selectedUser.avatar ? 'none' : 'flex' }} />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold text-white mb-2">{selectedUser.name}</h2>
                                        <div className="flex items-center space-x-4 text-gray-400 mb-3">
                                            <span className="flex items-center text-sm">
                                                <Shield className="h-4 w-4 mr-1" />
                                                ID: {selectedUser.discordId}
                                            </span>
                                            <span className="flex items-center text-sm">
                                                <Calendar className="h-4 w-4 mr-1" />
                                                Membro desde {formatDate(selectedUser.joinDate)}
                                            </span>
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            <Badge className={`${selectedUser.accessLevel === 'admin' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                                selectedUser.accessLevel.includes('head') ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                                    selectedUser.accessLevel !== 'user' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                                        'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                                }`}>
                                                {selectedUser.accessLevel === 'admin' && <Crown className="h-3 w-3 mr-1" />}
                                                {selectedUser.role}
                                            </Badge>
                                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                                                <Users className="h-3 w-3 mr-1" />
                                                Membro desde {formatDate(selectedUser.joinDate)}
                                            </Badge>
                                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                                <AlertTriangle className="h-3 w-3 mr-1" />
                                                {(selectedUser.activePunishments?.length || 0) + (selectedUser.history?.length || 0)} Punições
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="space-y-6">
                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center justify-between">
                                        <span className="flex items-center">
                                            <Mail className="h-5 w-5 mr-2 text-blue-400" />
                                            Informações de Contato
                                        </span>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={handleTogglePrivacy}
                                            className="text-gray-400 hover:text-white"
                                        >
                                            {showPrivateInfo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    {showPrivateInfo ? (
                                        <>
                                            <div>
                                                <p className="text-gray-500 mb-1">Email</p>
                                                <p className="text-white font-medium">
                                                    {selectedUser.email ? selectedUser.email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : 'Não informado'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 mb-1">Telefone</p>
                                                <p className="text-white font-medium">
                                                    {selectedUser.phone ? selectedUser.phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) *****-$3') : 'Não informado'}
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            <p className="mb-2">Informações Ocultas</p>
                                            <Button
                                                size="sm"
                                                onClick={handleTogglePrivacy}
                                                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                                            >
                                                Consultar
                                            </Button>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-gray-500 mb-1">Idioma</p>
                                        <p className="text-white font-medium">{selectedUser.language || 'pt-BR'}</p>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center justify-between">
                                        <span className="flex items-center">
                                            <MapPin className="h-5 w-5 mr-2 text-emerald-400" />
                                            Servidores ({editedCities.length})
                                        </span>
                                        {!isEditingServers ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setIsEditingServers(true)}
                                                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                                            >
                                                <Edit className="h-4 w-4 mr-1" />
                                                Gerenciar
                                            </Button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={handleSaveServers}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    <Save className="h-4 w-4 mr-1" />
                                                    Salvar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setIsEditingServers(false);
                                                        setEditedCities(selectedUser.cities || []);
                                                    }}
                                                    className="border-gray-600"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {isEditingServers && (
                                        <div className="mb-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                                            <h4 className="text-sm font-medium text-white mb-2">Adicionar Cidade</h4>
                                            <div className="space-y-2">
                                                <Input
                                                    placeholder="Nome da Cidade"
                                                    value={newCity.city}
                                                    onChange={(e) => setNewCity({ ...newCity, city: e.target.value })}
                                                    className="bg-black/20 border-gray-600 text-white text-sm"
                                                />
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Input
                                                        placeholder="Nome do Personagem"
                                                        value={newCity.characterName}
                                                        onChange={(e) => setNewCity({ ...newCity, characterName: e.target.value })}
                                                        className="bg-black/20 border-gray-600 text-white text-sm"
                                                    />
                                                    <Input
                                                        placeholder="ID do Jogador"
                                                        value={newCity.playerId}
                                                        onChange={(e) => setNewCity({ ...newCity, playerId: e.target.value })}
                                                        className="bg-black/20 border-gray-600 text-white text-sm"
                                                    />
                                                </div>
                                                <Button
                                                    onClick={handleAddCity}
                                                    className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-xs"
                                                >
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    Adicionar
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {editedCities.length > 0 ? (
                                        editedCities.map((city, idx) => (
                                            <div key={idx} className="bg-gray-900/50 p-3 rounded-lg border border-gray-700/50 relative group">
                                                {isEditingServers && (
                                                    <Button
                                                        size="icon"
                                                        variant="destructive"
                                                        onClick={() => handleRemoveCity(idx)}
                                                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                )}

                                                {isEditingServers ? (
                                                    <div className="space-y-2 pr-8">
                                                        <Input
                                                            value={city.city}
                                                            onChange={(e) => {
                                                                const newCities = [...editedCities];
                                                                newCities[idx].city = e.target.value;
                                                                setEditedCities(newCities);
                                                            }}
                                                            className="h-7 bg-black/20 border-gray-600 text-white text-xs"
                                                        />
                                                        <Input
                                                            value={city.characterName}
                                                            onChange={(e) => {
                                                                const newCities = [...editedCities];
                                                                newCities[idx].characterName = e.target.value;
                                                                setEditedCities(newCities);
                                                            }}
                                                            className="h-7 bg-black/20 border-gray-600 text-white text-xs"
                                                        />
                                                        <Input
                                                            value={city.playerId}
                                                            onChange={(e) => {
                                                                const newCities = [...editedCities];
                                                                newCities[idx].playerId = e.target.value;
                                                                setEditedCities(newCities);
                                                            }}
                                                            className="h-7 bg-black/20 border-gray-600 text-white text-xs"
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                                <span className="font-bold text-white text-lg">{city.characterName}</span>
                                                                <p className="text-xs text-gray-400">ID: {city.playerId}</p>
                                                            </div>
                                                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                                                {city.city}
                                                            </Badge>
                                                        </div>

                                                        {selectedUser.activePunishments?.some(p => p.playerId === city.playerId && p.status !== 'revoked' && p.status !== 'expired') ? (
                                                            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">
                                                                <AlertCircle className="h-3 w-3 inline mr-1" />
                                                                Punição Ativa
                                                            </div>
                                                        ) : (
                                                            <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-xs text-green-300">
                                                                <CheckCircle className="h-3 w-3 inline mr-1" />
                                                                Regular
                                                            </div>
                                                        )}

                                                        <div className="text-sm space-y-1 mt-2">
                                                            <p className="text-gray-400">
                                                                <span className="text-gray-500">Personagem:</span> {city.characterName}
                                                            </p>
                                                            <p className="text-gray-400">
                                                                <span className="text-gray-500">ID:</span> {city.playerId}
                                                            </p>
                                                            {city.joinedAt && (
                                                                <p className="text-gray-400">
                                                                    <span className="text-gray-500">Entrou em:</span> {formatDate(city.joinedAt)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">Nenhum servidor registrado</p>
                                    )}
                                </CardContent>
                            </Card>

                            {selectedUser.frozenData && (
                                <Card className="bg-gray-800/50 border-yellow-700/50">
                                    <CardHeader>
                                        <CardTitle className="text-white flex items-center">
                                            <Shield className="h-5 w-5 mr-2 text-yellow-400" />
                                            Dados Congelados (RP)
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3 text-sm">
                                        <div>
                                            <p className="text-gray-500 mb-1">Servidor Inicial</p>
                                            <p className="text-white font-medium">{selectedUser.frozenData.servidor || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">Player ID</p>
                                            <p className="text-white font-medium">{selectedUser.frozenData.playerId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">Nickname Inicial</p>
                                            <p className="text-white font-medium">{selectedUser.frozenData.initialNickname || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 mb-1">Congelado em</p>
                                            <p className="text-white font-medium">{formatDate(selectedUser.frozenData.frozenAt)}</p>
                                        </div>
                                        {selectedUser.frozenData.lastModifiedBy && (
                                            <div className="pt-2 border-t border-gray-700">
                                                <p className="text-yellow-400 text-xs">
                                                    Última modificação por Admin em {formatDate(selectedUser.frozenData.lastModifiedAt)}
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        <div className="lg:col-span-2 space-y-6">
                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center justify-between">
                                        <span className="flex items-center">
                                            <FileText className="h-5 w-5 mr-2 text-purple-400" />
                                            Anotações da Staff
                                        </span>
                                        {!isEditingNotes ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setIsEditingNotes(true)}
                                                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                                            >
                                                <Edit className="h-4 w-4 mr-1" />
                                                Editar
                                            </Button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={handleSaveNotes}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    <Save className="h-4 w-4 mr-1" />
                                                    Salvar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setIsEditingNotes(false);
                                                        setTempNotes(staffNotes);
                                                    }}
                                                    className="border-gray-600"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {isEditingNotes ? (
                                        <Textarea
                                            value={tempNotes}
                                            onChange={(e) => setTempNotes(e.target.value)}
                                            placeholder="Adicione anotações sobre este usuário (visível apenas para staff)..."
                                            className="bg-gray-900/50 border-gray-600 text-white min-h-[120px]"
                                        />
                                    ) : (
                                        <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700/50 min-h-[120px]">
                                            {staffNotes ? (
                                                <p className="text-gray-300 whitespace-pre-wrap">{staffNotes}</p>
                                            ) : (
                                                <p className="text-gray-500 italic">Nenhuma anotação registrada</p>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center">
                                        <History className="h-5 w-5 mr-2 text-orange-400" />
                                        Histórico de Perfil ({userHistory.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {userHistory.length > 0 ? (
                                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                            {userHistory.map((entry, idx) => (
                                                <div key={idx} className="bg-gray-900/50 p-3 rounded-lg border border-gray-700/50">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="h-4 w-4 text-gray-500" />
                                                            <span className="text-sm text-gray-400">{formatDate(entry.timestamp)}</span>
                                                        </div>
                                                        <Badge variant="outline" className="border-gray-600 text-gray-300 text-xs">
                                                            {getChangeTypeLabel(entry.type)}
                                                        </Badge>
                                                    </div>
                                                    {entry.type === 'staff_note' || entry.type === 'privacy_access' || entry.type === 'server_update' || entry.type === 'punishment_request' || entry.type === 'punishment_applied' ? (
                                                        <p className="text-sm text-gray-300">
                                                            <span className="text-purple-400 font-medium">{entry.staffName}</span> {entry.action}
                                                        </p>
                                                    ) : (
                                                        <div className="text-sm space-y-1">
                                                            <p className="text-gray-300">
                                                                <span className="text-gray-500">De:</span> <span className="text-red-400">{entry.oldValue || 'N/A'}</span>
                                                            </p>
                                                            <p className="text-gray-300">
                                                                <span className="text-gray-500">Para:</span> <span className="text-green-400">{entry.newValue || 'N/A'}</span>
                                                            </p>
                                                            {entry.metadata?.isAdminChange && (
                                                                <p className="text-yellow-400 text-xs mt-1">
                                                                    Modificado por Admin: {entry.metadata.adminName}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-8">Nenhuma mudança registrada</p>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardHeader>
                                    <CardTitle className="text-white flex items-center">
                                        <Ticket className="h-5 w-5 mr-2 text-blue-400" />
                                        Histórico de Tickets ({userTickets.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {userTickets.length > 0 ? (
                                        <div className="space-y-3">
                                            {userTickets.map((ticket) => (
                                                <Card
                                                    key={ticket.id}
                                                    className="bg-gray-900/30 border-gray-700 hover:bg-gray-800/50 transition-all cursor-pointer"
                                                    onClick={() => navigate(`/staff/tickets/${ticket.id}`)}
                                                >
                                                    <CardContent className="p-4 flex items-center justify-between">
                                                        <div className="flex items-center space-x-4">
                                                            <div className="p-2 bg-gray-800 rounded-lg">
                                                                <Ticket className="h-5 w-5 text-gray-400" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-medium text-white">{ticket.title}</h4>
                                                                <p className="text-sm text-gray-400">
                                                                    {formatDate(ticket.createdAt)} • {ticket.category}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Badge variant="outline" className="border-gray-600 text-gray-300">
                                                            {ticket.status}
                                                        </Badge>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 bg-gray-900/20 rounded-2xl border border-gray-800">
                                            <Ticket className="h-10 w-10 text-gray-600 mx-auto mb-2" />
                                            <p className="text-gray-400">Este usuário não possui tickets.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </motion.div >
            )
            }

            {/* Modal de Punição Multi-Step */}
            <Dialog open={isPunishmentModalOpen} onOpenChange={setIsPunishmentModalOpen}>
                <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold flex items-center">
                            <Gavel className="h-6 w-6 mr-2 text-red-500" />
                            Aplicar Ação Disciplinar
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            {punishmentStep === 1 && "Selecione o perfil alvo da ação"}
                            {punishmentStep === 2 && "Escolha o tipo de punição"}
                            {punishmentStep === 3 && "Preencha os detalhes da ação"}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4">
                        {/* Step 1: Seleção de Perfil */}
                        {punishmentStep === 1 && (
                            <div className="space-y-4">
                                <Label>Selecione o Perfil / Personagem</Label>
                                <div className="grid gap-3">
                                    {selectedUser?.cities?.map((city, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => setPunishmentData({
                                                ...punishmentData,
                                                serverProfileId: `${city.city}|${city.playerId}|${city.characterName}`
                                            })}
                                            className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center justify-between ${punishmentData.serverProfileId === `${city.city}|${city.playerId}|${city.characterName}`
                                                ? 'bg-blue-600/20 border-blue-500'
                                                : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                                                }`}
                                        >
                                            <div>
                                                <p className="font-bold text-white">{city.characterName}</p>
                                                <p className="text-sm text-gray-400">{city.city} - ID: {city.playerId}</p>
                                            </div>
                                            {punishmentData.serverProfileId === `${city.city}|${city.playerId}|${city.characterName}` && (
                                                <CheckCircle className="h-5 w-5 text-blue-400" />
                                            )}
                                        </div>
                                    ))}
                                    {(!selectedUser?.cities || selectedUser.cities.length === 0) && (
                                        <p className="text-center text-gray-500 py-4">Nenhum perfil encontrado.</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2: Tipo de Punição */}
                        {punishmentStep === 2 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {punishmentTypes.map((type) => (
                                    <div
                                        key={type.value}
                                        onClick={() => setPunishmentData({ ...punishmentData, type: type.value })}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center justify-between ${punishmentData.type === type.value
                                            ? `bg-${type.color}-600/20 border-${type.color}-500`
                                            : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                                            }`}
                                    >
                                        <span className="font-medium text-white">{type.label}</span>
                                        {punishmentData.type === type.value && (
                                            <CheckCircle className={`h-5 w-5 text-${type.color}-400`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Step 3: Detalhes */}
                        {punishmentStep === 3 && (
                            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>WL (Opcional)</Label>
                                        <Input
                                            value={punishmentData.wl}
                                            onChange={(e) => setPunishmentData({ ...punishmentData, wl: e.target.value })}
                                            placeholder="Número da WL"
                                            className="bg-gray-900 border-gray-600"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Prioridade</Label>
                                        <Select
                                            value={punishmentData.priority}
                                            onValueChange={(val) => setPunishmentData({ ...punishmentData, priority: val })}
                                        >
                                            <SelectTrigger className="bg-gray-900 border-gray-600">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                                <SelectItem value="baixa">Baixa</SelectItem>
                                                <SelectItem value="normal">Normal</SelectItem>
                                                <SelectItem value="alta">Alta</SelectItem>
                                                <SelectItem value="critica">Crítica</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Motivo *</Label>
                                    <Textarea
                                        value={punishmentData.reason}
                                        onChange={(e) => setPunishmentData({ ...punishmentData, reason: e.target.value })}
                                        placeholder="Descreva o motivo da punição..."
                                        className="bg-gray-900 border-gray-600 min-h-[100px]"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Anexos (Opcional)</Label>
                                    <Input
                                        value={punishmentData.attachments}
                                        onChange={(e) => setPunishmentData({ ...punishmentData, attachments: e.target.value })}
                                        placeholder="Links de prints ou vídeos"
                                        className="bg-gray-900 border-gray-600"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Prazo</Label>
                                    <RadioGroup
                                        value={punishmentData.deadlineType}
                                        onValueChange={(val) => setPunishmentData({ ...punishmentData, deadlineType: val })}
                                        className="flex gap-4 mb-2"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="indefinite" id="indefinite" />
                                            <Label htmlFor="indefinite">Indeterminado</Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="date" id="date" />
                                            <Label htmlFor="date">Data Específica</Label>
                                        </div>
                                    </RadioGroup>
                                    {punishmentData.deadlineType === 'date' && (
                                        <Input
                                            type="date"
                                            value={punishmentData.deadlineDate}
                                            onChange={(e) => setPunishmentData({ ...punishmentData, deadlineDate: e.target.value })}
                                            className="bg-gray-900 border-gray-600"
                                        />
                                    )}
                                </div>

                                <div className="space-y-2 pt-4 border-t border-gray-700">
                                    <Label>Encaminhar para Departamento (Apenas para "Encaminhar")</Label>
                                    <Select
                                        value={punishmentData.targetDepartment}
                                        onValueChange={(val) => setPunishmentData({ ...punishmentData, targetDepartment: val })}
                                    >
                                        <SelectTrigger className="bg-gray-900 border-gray-600">
                                            <SelectValue placeholder="Selecione o departamento" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                            {departments.map((dept) => (
                                                <SelectItem key={dept.value} value={dept.value}>
                                                    {dept.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex justify-between sm:justify-between">
                        {punishmentStep > 1 ? (
                            <Button
                                variant="outline"
                                onClick={() => setPunishmentStep(punishmentStep - 1)}
                                className="border-gray-600 text-gray-300"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Voltar
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={() => setIsPunishmentModalOpen(false)}
                                className="border-gray-600 text-gray-300"
                            >
                                Cancelar
                            </Button>
                        )}

                        {punishmentStep < 3 ? (
                            <Button
                                onClick={() => {
                                    if (punishmentStep === 1 && !punishmentData.serverProfileId) return alert('Selecione um perfil');
                                    if (punishmentStep === 2 && !punishmentData.type) return alert('Selecione um tipo');
                                    setPunishmentStep(punishmentStep + 1);
                                }}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Próximo
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        ) : (
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleApplyDirectly}
                                    variant="secondary"
                                    className="bg-green-600 hover:bg-green-700 text-white border-none"
                                >
                                    <CheckSquare className="h-4 w-4 mr-2" />
                                    Já Aplicada
                                </Button>
                                <Button
                                    onClick={handleForwardPunishment}
                                    className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    Encaminhar
                                </Button>
                            </div>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default StaffPlayerLookup;
